import React from "react";
import { Link, Navigate, useLocation } from "react-router-dom";
import PageShell from "@/components/PageShell";
import { BRAND } from "@/lib/brand";

const OFFICE = { city: BRAND.india_address, email: BRAND.email, phone: BRAND.phone_in };

const CONTENT = {
    "privacy-policy": {
        title: "Privacy Policy",
        eyebrow: "Effective 1 January 2026",
        sections: [
            { h: "1. What we collect",
              p: "We collect only the information you volunteer — name, phone, email, city, and the property preferences you share when enquiring, booking a site visit, or applying to our Channel Partner Programme." },
            { h: "2. How we use it",
              p: "Solely to advise you on properties, schedule visits, share brochures, and follow up with relationship-manager support. Nothing else — no third-party ad networks, no dark-pattern retargeting." },
            { h: "3. Who we share it with",
              p: "The developer of any project you specifically request information on, our own KYC vendor for the mandatory legal check, and our email delivery partner (Resend). We do not sell data." },
            { h: "4. Retention",
              p: "Enquiry data is retained for 24 months so we can serve you across the lifecycle of a property decision. You may request deletion any time by writing to " + OFFICE.email + "." },
            { h: "5. Your rights",
              p: "You may access, correct, or delete your personal data at any time. We honour requests within 7 business days." },
            { h: "6. Contact us",
              p: `Any questions on this policy — email ${OFFICE.email} or call ${OFFICE.phone}.` },
        ],
    },
    "terms-conditions": {
        title: "Terms & Conditions",
        eyebrow: "Effective 1 January 2026",
        sections: [
            { h: "1. Nature of service",
              p: "EBG Realty is a licensed real-estate advisory. We act on behalf of the buyer, matching them with vetted developer inventory. We do not warrant future price appreciation or possession dates published by developers." },
            { h: "2. Property information",
              p: "All information on this website — including prices, availability, floor plans, dimensions, and images — is sourced from developers and best-effort verified. It is indicative and subject to change without notice. Always verify final details in the sale-agreement before booking." },
            { h: "3. Bookings & tokens",
              p: "Any token or booking amount is paid directly to the developer through their account. EBG Realty does not receive or hold consumer funds." },
            { h: "4. Channel Partner listings",
              p: "Listings submitted by Channel Partners appear only after admin approval, but factual accuracy remains the partner's responsibility. We remove listings we find to be misleading." },
            { h: "5. Limitation of liability",
              p: "To the maximum extent permitted by law, EBG Realty is not liable for any indirect or consequential loss arising from decisions taken based on information published here." },
            { h: "6. Governing law",
              p: "These terms are governed by the laws of India. Any dispute is subject to the exclusive jurisdiction of the courts at Mumbai." },
        ],
    },
    "cookie-policy": {
        title: "Cookie Policy",
        eyebrow: "Effective 1 January 2026",
        sections: [
            { h: "1. What is a cookie",
              p: "A cookie is a small text file placed on your device to help a website function or remember your preferences." },
            { h: "2. Cookies we set",
              p: "Strictly necessary cookies for authentication (JWT tokens, saved-property state) and anonymised analytics via first-party cookies only. No third-party advertising cookies." },
            { h: "3. How to control them",
              p: "Most browsers let you block or delete cookies via Settings → Privacy. Blocking essential cookies may sign you out of your portal." },
            { h: "4. Do-Not-Track",
              p: "We honour DNT: if your browser sends the header we skip loading our analytics cookie for that session." },
        ],
    },
    "rera-disclaimer": {
        title: "RERA Disclaimer",
        eyebrow: "Real Estate (Regulation and Development) Act, 2016",
        sections: [
            { h: "Registration status",
              p: "EBG Realty is a licensed real-estate agent. Every project on this website carries the developer's RERA registration number where applicable. Where a project pre-dates RERA in its state, the same is disclosed on the property detail page." },
            { h: "Buyer advice",
              p: "Before booking any property, please verify the developer's RERA registration number, project completion status, and encumbrance certificate at your state RERA portal." },
            { h: "Marketing materials",
              p: "Artistic impressions, elevation drawings, and unit dimensions displayed on this website are indicative. Actual delivered product is per the sanctioned plan and sale-agreement." },
            { h: "Grievance officer",
              p: `Any RERA-related concern — write to us at ${OFFICE.email} with the property name and your booking reference. We escalate within 48 hours.` },
        ],
    },
    "careers": {
        title: "Careers at EBG Realty",
        eyebrow: "We're hiring",
        sections: [
            { h: "Our promise",
              p: "We are building India's most trusted legal-verified brokerage platform. We move fast, we ship quality, and we take our clients' money as seriously as our own." },
            { h: "Open roles",
              p: "Senior Real-Estate Advisors (Mumbai, Bengaluru, Delhi NCR) · Legal Analysts · Channel Partner Managers · Product Designers · Full-Stack Engineers." },
            { h: "How to apply",
              p: `Email a short intro and a CV/portfolio link to careers@ebgrealty.com — we reply to every application within 5 business days.` },
        ],
    },
};

// Simple auto-generated sitemap
const SITEMAP_LINKS = [
    { g: "Explore",  items: [
        { l: "Home",       to: "/" },
        { l: "Properties", to: "/properties" },
        { l: "Insights",   to: "/insights" },
        { l: "About",      to: "/about" },
        { l: "Services",   to: "/services" },
        { l: "Legal Check", to: "/legal-check" },
        { l: "Contact",    to: "/contact" },
    ] },
    { g: "For Partners",  items: [
        { l: "Channel Partner Programme", to: "/partner-registration" },
        { l: "Partner Sign In",            to: "/login?role=partner" },
    ] },
    { g: "Legal", items: [
        { l: "Privacy Policy",    to: "/privacy-policy" },
        { l: "Terms & Conditions", to: "/terms-conditions" },
        { l: "Cookie Policy",     to: "/cookie-policy" },
        { l: "RERA Disclaimer",   to: "/rera-disclaimer" },
    ] },
    { g: "Company", items: [
        { l: "Careers", to: "/careers" },
    ] },
];

export default function Legal() {
    const { pathname } = useLocation();
    const slug = pathname.replace(/^\//, "");
    if (slug === "sitemap") return <SitemapPage/>;
    const doc = CONTENT[slug];
    if (!doc) return <Navigate to="/" replace/>;
    return (
        <PageShell navVariant="light">
            <section className="max-w-[900px] mx-auto px-6 md:px-10 pt-40 pb-24" data-testid={`legal-${slug}`}>
                <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]">{doc.eyebrow}</div>
                <h1 className="font-serif text-4xl md:text-5xl lg:text-[56px] mt-4 leading-[1.05]">{doc.title}</h1>
                <div className="w-14 h-px bg-[var(--ebg-gold)] mt-6"/>
                <div className="mt-10 space-y-8">
                    {doc.sections.map((s, i) => (
                        <div key={i}>
                            <h2 className="font-serif text-xl md:text-2xl text-black">{s.h}</h2>
                            <p className="text-[15px] leading-relaxed text-black/70 mt-3">{s.p}</p>
                        </div>
                    ))}
                </div>
                <p className="text-[12px] text-black/45 mt-14">This document is a plain-language summary and does not replace independent legal advice. For questions write to us at {OFFICE.email}.</p>
            </section>
        </PageShell>
    );
}

function SitemapPage() {
    return (
        <PageShell navVariant="light">
            <section className="max-w-[1100px] mx-auto px-6 md:px-10 pt-40 pb-24" data-testid="legal-sitemap">
                <div className="text-[10px] uppercase tracking-[0.32em] font-bold text-[var(--ebg-gold)]">Site Navigation</div>
                <h1 className="font-serif text-4xl md:text-5xl mt-4">Sitemap</h1>
                <div className="w-14 h-px bg-[var(--ebg-gold)] mt-6"/>
                <div className="mt-10 grid md:grid-cols-4 gap-8">
                    {SITEMAP_LINKS.map((g) => (
                        <div key={g.g}>
                            <div className="text-[10px] uppercase tracking-[0.28em] font-bold text-black/50 mb-3">{g.g}</div>
                            <ul className="space-y-2">
                                {g.items.map((it) => (
                                    <li key={it.to}><Link to={it.to} className="text-[14px] text-black/80 hover:text-[var(--ebg-gold)]">{it.l}</Link></li>
                                ))}
                            </ul>
                        </div>
                    ))}
                </div>
            </section>
        </PageShell>
    );
}
